import { Menu, MenuButton, MenuItems, Transition } from '@headlessui/react'
import { useRollbar } from '@rollbar/react'
import { Fragment, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { mutate } from 'swr'
import Confirmation from 'shared/components/confirmation-modal'
import CloseIcon from 'shared/icons/close-icon'
import PenOutlineIcon from 'shared/icons/pen-outline-icon'
import PinIcon from 'shared/icons/pin-icon'
import ThreeDotsNavIcon from 'shared/icons/three-dots-nav-icon'
import TrashOutlineIcon from 'shared/icons/trash-outline-icon'
import { useDeleteComment } from 'modules/comments/api/delete-comment'
import { usePinComment } from 'modules/comments/api/pin-comment'
import { useUnpinComment } from 'modules/comments/api/unpin-comment'
import { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import {
  deleteCommentInLinkedList,
  findOptimalCommentPosition,
  insertCommentToLinkedList,
} from 'modules/comments/utils/comments-linked-list'
import { isCommunityExtra } from 'modules/comments/utils/is-community-extra'
import { getCommentsCountKey } from 'modules/community/api/get-comments-count'
import * as postsApi from 'modules/community/api/posts-api'
import { ACTIVITY_CLEANUP_PERIOD_DURATION } from 'modules/community/const/post-consts'
import { BanPeriodUnitEnum } from 'modules/community/enums/member-enum'
import { RemoveRecentActivitiesCheckbox } from 'modules/post/remove-recent-activities-checkbox'
import ActionsMenuItem from './actions-menu-items'

interface CommentActionsProps {
  onEdit: () => void
  className?: string
  commentData: CommentInterface
}

export default function CommentActions({ onEdit, className, commentData }: CommentActionsProps) {
  const { t } = useTranslation()
  const rollbar = useRollbar()
  const {
    extra,
    commentsList,
    setComments,
    updateReplies,
    repliesList,
    pinnedComments,
    setPinnedComments,
    currentSortOption,
  } = useCommentsContext()
  const deleteCommentMutation = useDeleteComment({ commentId: commentData.id })
  const pinCommentMutation = usePinComment({ commentId: commentData.id })
  const unpinCommentMutation = useUnpinComment({ commentId: commentData.id })

  const [removeRecentActivitiesChecked, setRemoveRecentActivitiesChecked] = useState(false)
  const [isDeleteAndBanUserOpened, setIsDeleteAndBanUserOpened] = useState(false)
  const [isDeleteCommentOpened, setIsDeleteCommentOpened] = useState(false)
  const [switchPinnedCommentModalOpened, setSwitchPinnedCommentModalOpened] = useState(false)

  const { canDeleteComment, canDeleteCommentAndBanAuthor, canUpdateComment, canPinComment } =
    commentData
  const isCommunityComments = isCommunityExtra(extra)
  const shouldRenderMenu = canDeleteComment || canUpdateComment || canDeleteCommentAndBanAuthor
  const isCanManagePinsAndNotReplyComment = canPinComment && Array.isArray(commentData.parent)

  const deleteComment = async () => {
    await deleteCommentMutation.trigger()
    if (deleteCommentMutation.error) return

    if (commentData.parent && !Array.isArray(commentData.parent)) {
      const parentID = commentData.parent.id
      updateReplies({
        commentId: parentID,
        newReplies: repliesList[parentID].replies?.filter(el => el.id !== commentData.id),
      })

      if (commentData.isPinned) {
        setPinnedComments(
          pinnedComments.map(comment =>
            comment.id === parentID
              ? { ...comment, totalReplies: comment.totalReplies - 1 }
              : comment,
          ),
        )
      } else {
        setComments(
          commentsList.map(comment =>
            comment.id === parentID
              ? { ...comment, totalReplies: comment.totalReplies - 1 }
              : comment,
          ),
        )
      }
    } else {
      if (commentData.isPinned) {
        setPinnedComments(deleteCommentInLinkedList(pinnedComments, commentData.id))
      } else {
        setComments(deleteCommentInLinkedList(commentsList, commentData.id))
      }
    }
    if (isCommunityComments) {
      mutate(getCommentsCountKey(extra.community.communityPath, [extra.community.postId]))
    }
  }

  const handleDeleteComment = async () => {
    await deleteComment()
      .then(() => {
        toast.success(t('comments.actions.delete_comment.success'))
      })
      .catch(error => {
        toast.error(t('comments.actions.delete_comment.failed'))
        rollbar.error(error as Error)
      })
  }

  const handleDeleteAndBanUser = async () => {
    if (!isCommunityComments) return
    try {
      await deleteComment()
    } catch (error) {
      toast.error(t('comments.actions.delete_comment.failed'))
      return rollbar.error(error as Error)
    }

    await postsApi
      .banUser(
        extra.community.communityPath,
        commentData.userId,
        removeRecentActivitiesChecked
          ? {
              deleteRecentActivityOptions: {
                periodUnit: BanPeriodUnitEnum.day,
                periodDuration: ACTIVITY_CLEANUP_PERIOD_DURATION,
              },
            }
          : undefined,
      )
      .then(() => {
        toast.success(t('comments.actions.delete_comment_and_ban_user.success'))
      })
      .catch(err => {
        toast.error(t('members.ban_failed_toast'))
        throw err
      })
  }

  // The maximum limit of pinned messages is 1. That's why they switch each other
  const handleSwitchPinnedComment = async () => {
    await pinCommentMutation
      .trigger()
      .then(() => {
        setComments(comments =>
          insertCommentToLinkedList(
            deleteCommentInLinkedList(comments, commentData.id),
            { ...pinnedComments[0], isPinned: false },
            findOptimalCommentPosition(comments, pinnedComments[0], currentSortOption),
          ),
        )
        setPinnedComments(
          insertCommentToLinkedList(
            deleteCommentInLinkedList(pinnedComments, pinnedComments[0]?.id),
            { ...commentData, isPinned: true },
            null,
          ),
        )
        setSwitchPinnedCommentModalOpened(false)
        toast.success(t('comments.actions.pin_comment.success'))
      })
      .catch(() => toast.error(t('core.error.internal_error_message')))
  }

  const handlePinComment = async () => {
    const isHaveAlreadyPinnedComment = !!pinnedComments.length
    if (!isHaveAlreadyPinnedComment) {
      await pinCommentMutation
        .trigger()
        .then(() => {
          setComments(comments => deleteCommentInLinkedList(comments, commentData.id))
          setPinnedComments(comments =>
            insertCommentToLinkedList(comments, { ...commentData, isPinned: true }, null),
          )
          toast.success(t('comments.actions.pin_comment.success'))
        })
        .catch(() => toast.error(t('core.error.internal_error_message')))
    } else {
      setSwitchPinnedCommentModalOpened(true)
    }
  }

  const handleUnpinComment = async () => {
    await unpinCommentMutation
      .trigger()
      .then(() => {
        const pinnedComment = { ...pinnedComments[0], isPinned: false }
        setPinnedComments([])
        setComments(comments => {
          const prevId = findOptimalCommentPosition(comments, pinnedComment, currentSortOption)
          return insertCommentToLinkedList(comments, pinnedComment, prevId)
        })
        toast.success(t('comments.actions.unpin_comment.success'))
      })
      .catch(() => toast.error(t('core.error.internal_error_message')))
  }

  if (!shouldRenderMenu) return null

  return (
    <>
      <Menu>
        <MenuButton className={className} onClick={e => e.stopPropagation()}>
          <div className="relative">
            <ThreeDotsNavIcon className="size-8 rounded-full text-text-secondary transition-colors duration-300 hover:bg-background-tertiary" />
          </div>
        </MenuButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <MenuItems
            as="ul"
            anchor={{ to: 'bottom end', gap: 10 }}
            className="z-10 flex w-max min-w-[144px] max-w-sm translate-x-1 flex-col rounded-[10px] bg-background-secondary py-3 shadow-[0px_4px_10px_0px_#00000040]"
            onClick={e => e.stopPropagation()}
          >
            {canDeleteComment && (
              <ActionsMenuItem onClick={() => setIsDeleteCommentOpened(true)}>
                <CloseIcon className="size-5 fill-text-primary group-hover:fill-text-primary/70" />
                {t('comments.actions.remove')}
              </ActionsMenuItem>
            )}
            {canUpdateComment && (
              <ActionsMenuItem onClick={onEdit}>
                <PenOutlineIcon className="size-5" />
                {t('comments.actions.edit')}
              </ActionsMenuItem>
            )}
            {canDeleteCommentAndBanAuthor && isCommunityComments && (
              <ActionsMenuItem onClick={() => setIsDeleteAndBanUserOpened(true)}>
                <TrashOutlineIcon className="size-5" />
                {t('comments.actions.remove_and_ban_user')}
              </ActionsMenuItem>
            )}
            {isCanManagePinsAndNotReplyComment &&
              (commentData?.isPinned ? (
                <ActionsMenuItem onClick={handleUnpinComment}>
                  <PinIcon className="size-5" />
                  {t('comments.actions.unpin')}
                </ActionsMenuItem>
              ) : (
                <ActionsMenuItem onClick={handlePinComment}>
                  <PinIcon className="size-5" />
                  {t('comments.actions.pin')}
                </ActionsMenuItem>
              ))}
          </MenuItems>
        </Transition>
      </Menu>

      {isDeleteAndBanUserOpened && (
        <Confirmation
          onConfirm={handleDeleteAndBanUser}
          onCloseModal={() => setIsDeleteAndBanUserOpened(false)}
          confirmationContent={
            <RemoveRecentActivitiesCheckbox
              label={t('settings.form.delete_comment_and_ban_user_confirmation.label')}
              memberDisplayName={commentData.userName}
              checked={removeRecentActivitiesChecked}
              onCheck={() => setRemoveRecentActivitiesChecked(prev => !prev)}
            />
          }
          errorMessageMap={{
            unprocessableContentError: 'community.error.error_user_already_banned',
          }}
        />
      )}
      {isDeleteCommentOpened && (
        <Confirmation
          onConfirm={handleDeleteComment}
          onCloseModal={() => setIsDeleteCommentOpened(false)}
          confirmationContent={t('comments.components.comments.delete_confirmation_label')}
        />
      )}
      {switchPinnedCommentModalOpened && (
        <Confirmation
          onConfirm={handleSwitchPinnedComment}
          onCloseModal={() => setSwitchPinnedCommentModalOpened(false)}
          confirmationContent={t('comments.actions.switch_pin_comment.confirmation.content')}
        />
      )}
    </>
  )
}
